import { flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { Divider, Space } from "antd";
import { useCallback, useEffect, useLayoutEffect } from "react";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTExternalLink } from "commonComponents/btWrappers/BTExternalLink/BTExternalLink";
import { BTIconExternalLink, BTIconLinkBreak } from "commonComponents/btWrappers/BTIcon";
import {
    areMultipleLinksSelected,
    getDOMPositionOfSelection,
    getLinkAtCursor,
} from "commonComponents/btWrappers/editor/core/editor.utils";
import { type IEditor } from "commonComponents/btWrappers/editor/core/useBTEditor.types";

import "./LinkOptionsOverlay.less";

interface ILinkOptionsOverlayProps {
    editor: IEditor;
}

export const LinkOptionsOverlay: React.FC<ILinkOptionsOverlayProps> = ({ editor }) => {
    const { tiptapEditor, editorState, editorStateDispatch } = editor;

    const { x, y, strategy, refs } = useFloating({
        middleware: [flip(), shift({ padding: 5 }), offset(5)],
    });

    const handleCalculatePosition = useCallback(() => {
        return getDOMPositionOfSelection(tiptapEditor);
    }, [tiptapEditor]);
    useLayoutEffect(() => {
        refs.setReference({ getBoundingClientRect: handleCalculatePosition });
    }, [handleCalculatePosition, refs]);

    useEffect(() => {
        const update = () => {
            refs.setReference({ getBoundingClientRect: handleCalculatePosition });
        };
        tiptapEditor.on("selectionUpdate", update);
        return () => {
            tiptapEditor.off("selectionUpdate", update);
        };
    }, [tiptapEditor, handleCalculatePosition, refs]);

    useEffect(() => {
        const update = () => {
            const show = tiptapEditor.isActive("link") && !areMultipleLinksSelected(tiptapEditor);
            editorStateDispatch({ type: "setShowLinkOptionsOverlay", payload: show });
        };
        // `transaction` is a superset of `selectionUpdate` (a selection change is itself a
        // transaction), so subscribing to it alone covers both content and selection changes
        // without firing the handler twice per change.
        tiptapEditor.on("transaction", update);
        // Sync initial visibility on mount, matching the old dep-based effect's first run.
        update();
        return () => {
            tiptapEditor.off("transaction", update);
        };
    }, [tiptapEditor, editorStateDispatch]);

    const handleBlur = useCallback(
        ({ event }: { event: FocusEvent }) => {
            if (
                refs.floating.current &&
                !refs.floating.current.contains(event.relatedTarget as Node)
            ) {
                editorStateDispatch({ type: "setShowLinkOptionsOverlay", payload: false });
            }
        },
        [editorStateDispatch, refs.floating],
    );

    useEffect(() => {
        tiptapEditor.on("blur", handleBlur);
        return () => {
            tiptapEditor.off("blur", handleBlur);
        };
    }, [tiptapEditor, handleBlur]);

    const handleOpenEditLinkForm = () => {
        editorStateDispatch({ type: "setShowLinkEditorOverlay", payload: true });
        editorStateDispatch({ type: "setShowLinkOptionsOverlay", payload: false });
    };

    const handleBreakLink = () => {
        tiptapEditor.chain().focus().unsetLink().run();
    };

    if (!editorState.showLinkOptions) {
        return null;
    }

    const linkAtCursor = getLinkAtCursor(tiptapEditor);
    return (
        <div
            ref={refs.setFloating}
            className="LinkOptionsOverlay"
            style={{ position: strategy, top: y ?? undefined, left: x ?? undefined }}
            tabIndex={0}
        >
            <Space size={2} split={<Divider type="vertical" className="OptionDivider" />}>
                <BTButtonDeprecated
                    id="editLink"
                    data-testid="editLink"
                    type="tertiary"
                    onClick={handleOpenEditLinkForm}
                >
                    Edit link
                </BTButtonDeprecated>
                <BTExternalLink href={linkAtCursor ?? undefined}>
                    <BTButtonDeprecated
                        id="openLink"
                        data-testid="openLink"
                        icon={<BTIconExternalLink />}
                        type="tertiary"
                    />
                </BTExternalLink>
                <BTButtonDeprecated
                    id="breakLink"
                    data-testid="breakLink"
                    icon={<BTIconLinkBreak />}
                    type="tertiary"
                    onClick={handleBreakLink}
                />
            </Space>
        </div>
    );
};
